import { useFeatureValue } from '@growthbook/growthbook-react';
import { ArrowBack } from '@mui/icons-material';
import { Box, TextField, Typography } from '@mui/material';
import * as R from 'ramda';
import { Dispatch, SetStateAction, useState } from 'react';

import { highFive, lumiqWhiteIcon } from '@/assets';
import {
  Button,
  ButtonVariants,
  ModalContentContainer,
  ProgressBar,
  RadioBoxButton,
} from '@/components';
import { ProductFeedbackQuestionIds } from '@/constants';
import { TrustPilotWidget } from '@/containers/TrustPilotWidget';
import { appStyles, theme } from '@/theme';

import {
  FEEDBACK_WIDGET_STEP,
  FeedbackSentiment,
  ProductFeedbackForm,
  Questions,
  Steps,
} from './types';

interface ProductFeedbackModalContentProps {
  form: ProductFeedbackForm;
  setForm: Dispatch<SetStateAction<ProductFeedbackForm>>;
  submitFeedback: ({ isDismissed }: { isDismissed?: boolean }) => Promise<void>;
  closeModal: () => void;
}

export const ProductFeedbackModalContent = ({
  form,
  setForm,
  submitFeedback,
  closeModal,
}: ProductFeedbackModalContentProps) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showFeedbackWidget =
    useFeatureValue('trustpilot-widget', false) &&
    form[ProductFeedbackQuestionIds.sentiment] ===
      FeedbackSentiment.VeryDisappointed;

  const steps = showFeedbackWidget
    ? [...Steps, FEEDBACK_WIDGET_STEP]
    : [...Steps];
  const currentStep = steps[stepIndex];

  const goToNextStep = () => {
    setStepIndex(stepIndex => Math.min(stepIndex + 1, steps.length - 1));
  };

  const goToPrevStep = () => {
    setStepIndex(stepIndex => Math.max(stepIndex - 1, 0));
  };

  return (
    <Box
      sx={{
        height: { xs: '80vh', md: '600px' },
        width: { xs: '100%', md: '800px' },
      }}>
      <ModalContentContainer
        onModalClose={() => submitFeedback({ isDismissed: true })}
        leftColumnWidth={280}
        leftColumnContents={
          <Box
            component="img"
            alt="lumiq logo"
            sx={{ width: 144, height: 144, m: 'auto' }}
            src={lumiqWhiteIcon}
          />
        }
        stretch>
        <Box
          sx={[
            {
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'auto',
            },
          ]}>
          <Box sx={styles.fullHeightColumn}>
            <Typography variant="h2" sx={{ mb: 1 }}>
              {currentStep === FEEDBACK_WIDGET_STEP
                ? 'We’re thrilled to hear that!'
                : currentStep === ProductFeedbackQuestionIds.followUpEmail
                  ? 'Thanks for your feedback!'
                  : 'Share your feedback'}
            </Typography>
            {currentStep === ProductFeedbackQuestionIds.sentiment && (
              <Typography variant="body2">
                We truly appreciate your feedback! We read every word to help us
                create a more valuable LumiQ experience.
              </Typography>
            )}
            <Box
              sx={{
                mt: 4,
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
              }}>
              {currentStep === ProductFeedbackQuestionIds.sentiment && (
                <Box sx={styles.fullHeightColumn}>
                  <Typography
                    variant={QUESTION_TEXT_VARIANT}
                    sx={styles.questionText}>
                    {Questions[ProductFeedbackQuestionIds.sentiment].question}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flexGrow: 1,
                      width: '100%',
                      gap: 1,
                    }}>
                    {Questions[
                      ProductFeedbackQuestionIds.sentiment
                    ].options?.map(option => (
                      <RadioBoxButton
                        key={option}
                        label={option}
                        checked={
                          form[ProductFeedbackQuestionIds.sentiment] === option
                        }
                        onChange={() =>
                          setForm({
                            ...form,
                            [ProductFeedbackQuestionIds.sentiment]: option,
                          })
                        }
                      />
                    ))}
                  </Box>
                  <Box sx={styles.buttonContainer}>
                    <Button
                      label="Continue"
                      skinny={false}
                      disabled={R.isNil(form[currentStep])}
                      onClick={goToNextStep}
                    />
                  </Box>
                  <Box sx={styles.progressBarContainer}>
                    <ProgressBar progressFraction={1 / 3} />
                    <Typography variant="caption1" sx={{ flexShrink: 0 }}>
                      1/3
                    </Typography>
                  </Box>
                </Box>
              )}
              {currentStep === ProductFeedbackQuestionIds.isRecommended && (
                <Box sx={styles.fullHeightColumn}>
                  <Typography
                    variant={QUESTION_TEXT_VARIANT}
                    sx={styles.questionText}>
                    {
                      Questions[ProductFeedbackQuestionIds.isRecommended]
                        .question
                    }
                  </Typography>
                  <Box
                    sx={[
                      {
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        width: '100%',
                        gap: 1,
                      },
                    ]}>
                    <RadioBoxButton
                      key="Yes"
                      label="Yes"
                      checked={
                        form[ProductFeedbackQuestionIds.isRecommended] === true
                      }
                      onChange={() => {
                        setForm({
                          ...form,
                          [ProductFeedbackQuestionIds.isRecommended]: true,
                        });
                      }}
                    />
                    <RadioBoxButton
                      key="No"
                      label="No"
                      checked={
                        form[ProductFeedbackQuestionIds.isRecommended] === false
                      }
                      onChange={() => {
                        setForm({
                          ...form,
                          [ProductFeedbackQuestionIds.isRecommended]: false,
                          // reset recommendReason on form if user changes isRecommended answer to No
                          [ProductFeedbackQuestionIds.recommendReason]:
                            undefined,
                        });
                      }}
                    />

                    {form[ProductFeedbackQuestionIds.isRecommended] && (
                      <Box sx={{ width: '100%', mt: 2 }}>
                        <Typography
                          variant={QUESTION_TEXT_VARIANT}
                          sx={styles.questionText}>
                          When you recommended LumiQ, how did you describe it?
                        </Typography>
                        <TextField
                          multiline
                          minRows={2}
                          maxRows={2}
                          placeholder="Your answer"
                          aria-label="Reason for recommendation"
                          fullWidth
                          value={
                            form[ProductFeedbackQuestionIds.recommendReason]
                          }
                          onChange={e =>
                            setForm({
                              ...form,
                              [ProductFeedbackQuestionIds.recommendReason]:
                                e.target.value,
                            })
                          }
                          sx={[
                            appStyles.scrollbar,
                            {
                              overflowY: 'scroll',
                              '& .MuiInputBase-root': {
                                borderRadius: 2,
                              },
                              '& .MuiInputBase-input': {
                                ...theme.typography.body2,
                              },
                            },
                          ]}
                        />
                      </Box>
                    )}
                  </Box>
                  <Box sx={styles.buttonContainer}>
                    <Button
                      startIcon={<ArrowBack />}
                      variant={ButtonVariants.SECONDARY}
                      skinny={false}
                      fullWidth={false}
                      onClick={goToPrevStep}
                    />
                    <Button
                      label="Continue"
                      skinny={false}
                      onClick={goToNextStep}
                    />
                  </Box>
                  <Box sx={styles.progressBarContainer}>
                    <ProgressBar progressFraction={2 / 3} />
                    <Typography variant="caption1" sx={{ flexShrink: 0 }}>
                      2/3
                    </Typography>
                  </Box>
                </Box>
              )}
              {currentStep === ProductFeedbackQuestionIds.suggestions && (
                <Box sx={styles.fullHeightColumn}>
                  <Typography
                    variant={QUESTION_TEXT_VARIANT}
                    sx={styles.questionText}>
                    {Questions[ProductFeedbackQuestionIds.suggestions].question}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flexGrow: 1,
                      width: '100%',
                      gap: 1,
                    }}>
                    <TextField
                      multiline
                      minRows={3}
                      maxRows={3}
                      placeholder="Your answer"
                      aria-label="Suggestions"
                      fullWidth
                      value={form[ProductFeedbackQuestionIds.suggestions]}
                      onChange={e =>
                        setForm({
                          ...form,
                          [ProductFeedbackQuestionIds.suggestions]:
                            e.target.value,
                        })
                      }
                      sx={[
                        appStyles.scrollbar,
                        {
                          overflowY: 'scroll',
                          '& .MuiInputBase-root': {
                            borderRadius: 2,
                          },
                          '& .MuiInputBase-input': {
                            ...theme.typography.body2,
                          },
                        },
                      ]}
                    />
                  </Box>
                  <Box sx={styles.buttonContainer}>
                    <Button
                      startIcon={<ArrowBack />}
                      variant={ButtonVariants.SECONDARY}
                      skinny={false}
                      fullWidth={false}
                      onClick={goToPrevStep}
                    />
                    <Button
                      label="Continue"
                      skinny={false}
                      onClick={goToNextStep}
                    />
                  </Box>
                  <Box sx={styles.progressBarContainer}>
                    <ProgressBar progressFraction={3 / 3} />
                    <Typography variant="caption1" sx={{ flexShrink: 0 }}>
                      3/3
                    </Typography>
                  </Box>
                </Box>
              )}
              {[
                FEEDBACK_WIDGET_STEP,
                ProductFeedbackQuestionIds.followUpEmail,
              ].includes(currentStep) && (
                <Box sx={styles.fullHeightColumn}>
                  <Box
                    component="img"
                    src={highFive}
                    sx={{ width: 100, alignSelf: 'center', mb: 2 }}
                  />
                  {currentStep === ProductFeedbackQuestionIds.followUpEmail && (
                    <>
                      <Typography
                        variant={QUESTION_TEXT_VARIANT}
                        sx={[styles.questionText]}>
                        {
                          Questions[ProductFeedbackQuestionIds.followUpEmail]
                            .question
                        }
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          flexGrow: 1,
                          width: '100%',
                          gap: 1,
                        }}>
                        <RadioBoxButton
                          key="Yes"
                          label="Yes, you may contact me"
                          checked={
                            form[ProductFeedbackQuestionIds.followUpEmail] ===
                            true
                          }
                          onChange={() => {
                            setForm({
                              ...form,
                              [ProductFeedbackQuestionIds.followUpEmail]: true,
                            });
                          }}
                        />
                        <RadioBoxButton
                          key="No"
                          label="No, thanks"
                          checked={
                            form[ProductFeedbackQuestionIds.followUpEmail] ===
                            false
                          }
                          onChange={() => {
                            setForm({
                              ...form,
                              [ProductFeedbackQuestionIds.followUpEmail]: false,
                            });
                          }}
                        />
                      </Box>
                      <Box sx={styles.buttonContainer}>
                        <Button
                          label="Done"
                          skinny={false}
                          isLoading={isSubmitting}
                          onClick={async () => {
                            try {
                              setIsSubmitting(true);
                              await submitFeedback({ isDismissed: false });
                              if (showFeedbackWidget) {
                                goToNextStep();
                              } else {
                                closeModal();
                              }
                            } finally {
                              setIsSubmitting(false);
                            }
                          }}
                        />
                      </Box>
                    </>
                  )}
                  {currentStep === FEEDBACK_WIDGET_STEP && (
                    <>
                      <Box
                        sx={{
                          mb: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}>
                        <Typography variant="h5">
                          It&apos;s amazing to hear that LumiQ has become a
                          must-have for you.
                        </Typography>
                        <Typography variant="h5">
                          Could you take 60 seconds to share your experience on
                          Trustpilot? It helps other accounting professionals
                          discover us.
                        </Typography>
                      </Box>
                      <TrustPilotWidget />
                      <Button
                        variant="secondary"
                        label="Not right now"
                        skinny={false}
                        onClick={closeModal}
                      />
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </ModalContentContainer>
    </Box>
  );
};

const QUESTION_TEXT_VARIANT = 'h4';

const styles = {
  fullHeightColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  questionText: {
    mb: 1,
  },
  progressBarContainer: {
    display: 'flex',
    gap: 2,
    alignItems: 'center',
    mt: 2,
  },
  buttonContainer: {
    display: 'flex',
    gap: 2,
    mt: 2,
  },
};
